import { useCallback, useEffect, useState } from 'react';
import {
  applyThemePreference,
  getResolvedTheme,
  getStoredThemePreference,
  setStoredThemePreference,
  type ResolvedTheme,
  type ThemePreference,
} from '../utils/theme';

export function useThemePreference() {
  const [preference, setPreferenceState] = useState<ThemePreference>(() => getStoredThemePreference());
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => getResolvedTheme(preference));

  const setPreference = useCallback((nextPreference: ThemePreference) => {
    setStoredThemePreference(nextPreference);
    setPreferenceState(nextPreference);
    setResolvedTheme(applyThemePreference(nextPreference));
  }, []);

  const toggleTheme = useCallback(() => {
    setPreference(resolvedTheme === 'dark' ? 'light' : 'dark');
  }, [resolvedTheme, setPreference]);

  useEffect(() => {
    setResolvedTheme(applyThemePreference(preference));
  }, [preference]);

  useEffect(() => {
    if (preference !== 'system' || typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      setResolvedTheme(applyThemePreference('system'));
    };

    media.addEventListener('change', handleChange);
    return () => media.removeEventListener('change', handleChange);
  }, [preference]);

  return { preference, resolvedTheme, setPreference, toggleTheme };
}
